import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import {
  CheckCircle2,
  Circle,
  Calendar,
  Briefcase,
  MessageSquare,
  User,
  ArrowRight,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const OnboardingChecklist = ({ dashboardData, onComplete }) => {
  const [checklist, setChecklist] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (dashboardData) {
      const items = generateChecklist(dashboardData);
      setChecklist(items);
      const completed = items.filter((item) => item.completed).length;
      setProgress((completed / items.length) * 100);
    }
  }, [dashboardData]);

  const generateChecklist = (data) => {
    // Check if company profile is properly filled out
    const hasCompanyProfile = !!(
      data?.client?.company_name && 
      data?.client?.industry && 
      data?.client?.primary_contact_name
    );
    
    const items = [
      {
        id: "profile",
        title: "Complete your profile",
        description: "Add your company info and contact details",
        icon: User,
        completed: hasCompanyProfile,
        link: "/portal/profile",
        priority: 1,
      },
      {
        id: "schedule",
        title: "Schedule your kickoff call",
        description: "Book time with your consultant to get started",
        icon: Calendar,
        completed: data?.has_scheduled_call || false,
        link: "/portal/booking",
        priority: 2,
      },
      {
        id: "projects",
        title: "Review your projects",
        description: "Check your assigned projects and time tracking",
        icon: Briefcase,
        completed: (data?.active_projects_count || 0) > 0,
        link: "/portal/my-projects",
        priority: 3,
      },
      {
        id: "chat",
        title: "Send your first message",
        description: "Introduce yourself to your consultant",
        icon: MessageSquare,
        completed: (data?.messages_count || 0) > 0,
        link: "/portal/chat",
        priority: 4,
      },
    ];

    return items.sort((a, b) => {
      // Sort: incomplete first, then by priority
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      return a.priority - b.priority;
    });
  };

  const allComplete = progress === 100;

  if (allComplete && onComplete) {
    return null; // Hide when complete
  }

  return (
    <Card className="border-rnb-teal/30 bg-gradient-to-br from-rnb-light to-white" data-testid="onboarding-checklist">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-rnb-teal" />
            <CardTitle className="text-lg text-rnb-navy">Get Started</CardTitle>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-rnb-teal">
              {Math.round(progress)}% complete
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="h-8 w-8 p-0"
              data-testid="collapse-onboarding-btn"
            >
              {isCollapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
        <Progress value={progress} className="h-2 mt-2" />
      </CardHeader>
      
      {!isCollapsed && (
        <CardContent className="pt-4">
          <div className="space-y-3">
            {checklist.map((item) => (
              <Link
                key={item.id}
                to={item.link}
                target={item.externalLink ? "_blank" : undefined}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  item.completed
                    ? "bg-green-50 border border-green-100"
                    : "bg-white border border-slate-100 hover:border-rnb-teal/30 hover:bg-slate-50"
                }`}
                data-testid={`onboarding-${item.id}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    item.completed
                      ? "bg-green-100 text-green-600"
                      : "bg-rnb-light text-rnb-gray"
                  }`}
                >
                  {item.completed ? (
                    <CheckCircle2 className="h-5 w-5" />
                  ) : (
                    <item.icon className="h-4 w-4" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-medium text-sm ${
                      item.completed ? "text-green-700 line-through" : "text-rnb-navy"
                    }`}
                  >
                    {item.title}
                  </p>
                  {!item.completed && (
                    <p className="text-xs text-rnb-gray truncate">{item.description}</p>
                  )}
                </div>
                {!item.completed && (
                  <ArrowRight className="h-4 w-4 text-rnb-gray flex-shrink-0" />
                )}
              </Link>
            ))}
          </div>

          {allComplete && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg text-center">
              <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="font-medium text-green-700">All set! You're ready to go.</p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 text-green-600"
                onClick={onComplete}
              >
                Dismiss checklist
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default OnboardingChecklist;
